import { AceLanguage, NotebookState, NotebookTypes } from '@mode/notebooks/contract';
import { isPresent } from '@mode/shared/util-js';
import { createSelector, createSelectorCreator, createStructuredSelector, defaultMemoize, Selector } from 'reselect';
/*
Contains legacy store selector paths
TODO: Will contain NgRx selectors once we migrate legacy notebook state to NgRx state
Based on selectors in notebook.selectors.ts
*/
import * as LegacyNotebookSelectors from '../notebook/legacy-notebook.selectors';
import { NormalizedNotebookCellRunResultArray } from '../notebook.types';
import { is } from 'immutable';
import { NotebookCellRunResultResource } from '../../../../domain/src/lib/notebook.resource';
import {
  NormalizedNotebookCellExRes,
  NormalizedNotebookCellRunResult,
} from '../../../../domain/src/lib/notebook.types';

const notebookPath = (state: any) => state?.client?.notebooks;

const cellPath = (state: any) => notebookPath(state)?.cells;

function vizPath(state: any) {
  return state.client.selectedReport.notebookViz;
}
function cellRunPath(state: any) {
  return notebookPath(state).cellRuns;
}

function immutableEqualityCheck(a: any, b: any): boolean {
  return is(a, b);
}

function getNormalizedCellRunResult(
  results: NormalizedNotebookCellRunResultArray,
  token: string
): NormalizedNotebookCellRunResult {
  return {
    token,
    result_type: results.resultTypes.get(token) ?? '',
    content_type: results.contentTypes.get(token) ?? '',
    date: results.dates.get(token) ?? '',
    content: results.contents.get(token) ?? '',
    resource: results.resources.get(token) ?? ({} as NotebookCellRunResultResource),
  };
}

function resultEqualityCheck(a: any, b: any): boolean {
  if (immutableEqualityCheck(a, b)) {
    return true;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    const xArr = a as NormalizedNotebookCellRunResult[];
    const yArr = b as NormalizedNotebookCellRunResult[];

    if (xArr.length === yArr.length) {
      return xArr.every(
        (x, ii) =>
          x.content === yArr[ii].content &&
          x.content_type === yArr[ii].content_type &&
          x.result_type === yArr[ii].result_type &&
          x.token === yArr[ii].token
      );
    } else {
      return false;
    }
  }

  if (a != null && b != null) {
    const x = a as NormalizedNotebookCellRunResult;
    const y = a as NormalizedNotebookCellRunResult;
    return (
      x.content === y.content &&
      x.content_type === y.content_type &&
      x.result_type === y.result_type &&
      x.token === y.token
    );
  }

  return false;
}

const createImmutableCheckSelector = createSelectorCreator(defaultMemoize, immutableEqualityCheck);
const createResultCheckSelector = createSelectorCreator(defaultMemoize, resultEqualityCheck);

export const runsExecutionCountsArray = (state: any) => cellRunPath(state).executionCounts;

export const cellFrozenArray = (state: any) => cellPath(state).frozen;

export const isEditing = (state: any) => notebookPath(state)?.editor?.isEditing;

export const cellContentsArray = (state: any) => cellPath(state).contents;
export const cellsContentTypesArray = (state: any) => cellPath(state).contentTypes;
export const cellCreatedAtArray = (state: any) => cellPath(state).createdAt;
export const cellExecutingArray = (state: any) => cellPath(state).executing;
export const cellFoldedArray = (state: any) => cellPath(state).folded;
export const cellLastRunTokensArray = (state: any) => cellPath(state).lastRunTokens;
export const cellMissingRunsArray = (state: any) => cellPath(state).missingRuns;
export const cellResourcesArray = (state: any) => cellPath(state).resources;

export const runsResultTokensArray = (state: any) => cellRunPath(state).resultTokens;
export const vizTokensArray = (state: any) => vizPath(state).tokens;
export const vizCellTokensArray = (state: any) => vizPath(state).cellTokens;
export const vizCellRunTokensArray = (state: any) => vizPath(state).cellRunTokens;
export const vizTitlesArray = (state: any) => vizPath(state).titles;
export const vizResourcesArray = (state: any) => vizPath(state).resources;
export const vizRunState = (state: any) => vizPath(state).runState;

export const selectedNotebookCellsExecutionCount = createSelector(runsExecutionCountsArray, (executionCounts) =>
  executionCounts.count()
);
export function createIsCellEditingSelector(cellToken: string): Selector<any, boolean> {
  return createSelector(
    LegacyNotebookSelectors.selectedCellToken,
    isEditing,
    (selectedToken: string | null, editing: boolean) => selectedToken === cellToken && editing
  );
}
export const selectedCellFrozen = createSelector(
  LegacyNotebookSelectors.selectedCellToken,
  cellFrozenArray,
  (cellToken, frozen) => frozen.get(cellToken)
);
export function createCellExecutionCountSelector(cellToken: string): Selector<any, number> {
  const lastRunToken = (state: any) => cellPath(state).lastRunTokens.get(cellToken);
  return createSelector(lastRunToken, runsExecutionCountsArray, (runToken, executionCounts) =>
    executionCounts.get(runToken)
  );
}

export function createMarkdownViewModeSelector(cellToken: string) {
  return createSelector(
    createIsCellEditingSelector(cellToken),
    (state: any) => cellsContentTypesArray(state).get(cellToken),
    (editing, contentType) => !editing && contentType === NotebookTypes.ContentType.Markdown
  );
}

export function createCellAceLangSelector(cellToken: string): Selector<any, AceLanguage> {
  return createSelector(
    LegacyNotebookSelectors.selectedNotebookType,
    cellsContentTypesArray,
    (notebookType: NotebookTypes.NotebookType, cellTypes: Map<string, NotebookTypes.ContentType>): AceLanguage => {
      const contentType = cellTypes?.get(cellToken);

      if (contentType === NotebookTypes.ContentType.Markdown) {
        return AceLanguage.Markdown;
      }
      switch (notebookType) {
        case NotebookTypes.NotebookType.R:
          return AceLanguage.R;
        default:
          return AceLanguage.Python;
      }
    }
  ) as unknown as Selector<any, AceLanguage>;
}
export const cellTokensForSelectedReport = createSelector(
  vizTokensArray,
  vizCellTokensArray,
  (tokens: any, cellTokens: any) => tokens.map((t: any) => cellTokens.get(t))
);
export function createNormalizedCellExResSelector(token: string): Selector<any, NormalizedNotebookCellExRes> {
  return createStructuredSelector({
    token: () => token, // Not sure if this is hacky or not
    content: (state: any) => cellContentsArray(state).get(token),
    content_type: (state: any) => cellsContentTypesArray(state).get(token),
    created_at: (state: any) => cellCreatedAtArray(state).get(token),
    frozen: (state: any) => cellFrozenArray(state).get(token),
    executing: (state: any) => cellExecutingArray(state).get(token),
    lastRunToken: (state: any) => cellLastRunTokensArray(state).get(token),
    folded: (state: any) => cellFoldedArray(state).get(token),
    missingRun: (state: any) => cellMissingRunsArray(state).has(token),
  });
}

export function createIsCellInLayoutSelector(cellToken: string): Selector<any, boolean> {
  return createSelector(
    cellTokensForSelectedReport,
    (cellsInReport: any): boolean =>
      !!(cellsInReport && typeof cellsInReport.has === 'function' && cellsInReport.has(cellToken))
  ) as unknown as Selector<any, boolean>;
}

export const selectedCellExecuting = createSelector(
  LegacyNotebookSelectors.selectedCellToken,
  cellExecutingArray,
  (cellToken, executing) => executing.get(cellToken) || false
);

export const selectedCellContent = createSelector(
  LegacyNotebookSelectors.selectedCellToken,
  cellContentsArray,
  (cellToken, contents) => contents.get(cellToken)
);

export const selectedCellIsInLayout = createSelector(
  LegacyNotebookSelectors.selectedCellToken,
  cellTokensForSelectedReport,
  (cellToken, reportCells) => reportCells.has(cellToken)
) as any;

export const selectedCellFolded = createSelector(
  LegacyNotebookSelectors.selectedCellToken,
  cellFoldedArray,
  (cellToken, folded) => folded.get(cellToken)
);
export function createNotebookStateSelector(notebookToken: string): Selector<any, NotebookState> {
  return createSelector(
    (state: any) => LegacyNotebookSelectors.notebookStatesArray(state).get(notebookToken),
    (state) => state
  );
}
export const createCellContentTypeSelector = (cellToken: string) => (state: any) =>
  state.client.notebooks.cells.contentTypes.get(cellToken);
export const canFreezeByCell = createSelector(
  (state: any, cellToken: string) => cellsContentTypesArray(state).get(cellToken),
  (contentType) => contentType !== NotebookTypes.ContentType.Markdown
);
export const isExportedByCell = createSelector(
  (state: any, cellToken: string) => cellResourcesArray(state).get(cellToken),
  (cellResource) => isPresent(cellResource.local_dataset_exported_at)
) as any;

export function selectedCellHasResult() {
  return createSelector(
    LegacyNotebookSelectors.selectedCellToken,
    LegacyNotebookSelectors.selectedCellContentType,
    cellLastRunTokensArray,
    runsResultTokensArray,
    (cellToken, contentType, lastRunTokens, resultTokens) =>
      cellToken &&
      contentType !== NotebookTypes.ContentType.Markdown &&
      lastRunTokens.has(cellToken) &&
      resultTokens.has(lastRunTokens.get(cellToken)) &&
      resultTokens.get(lastRunTokens.get(cellToken)).size > 0
  ) as any;
}

export function createCanCellRunSelector(notebookToken: string): Selector<any, boolean> {
  return createSelector(
    createNotebookStateSelector(notebookToken),
    LegacyNotebookSelectors.selectedCellToken,
    selectedCellExecuting,
    LegacyNotebookSelectors.selectedCellContentType,
    selectedCellContent, // a super minor optimization would be to make a selectedCellContentEmpty selector
    selectedCellFrozen,
    (state, cellToken, executing, contentType, content, isFrozen) =>
      // only when notebook is ready and cell isn't already running. Must also be nonempty (ignoring whitespace)
      state === NotebookState.Ready &&
      cellToken != null &&
      !executing &&
      !isFrozen &&
      content != null &&
      contentType !== NotebookTypes.ContentType.Markdown &&
      content.trim().length > 0
  ) as unknown as Selector<any, boolean>;
}

export const selectedNotebookCellTokens = createSelector(
  LegacyNotebookSelectors.selectedNotebookToken,
  LegacyNotebookSelectors.notebookCellTokensArray,
  (token, cellTokens) => cellTokens.get(token)
);

export const selectedNextCellToken = createSelector(
  LegacyNotebookSelectors.selectedCellToken,
  selectedNotebookCellTokens,
  (cellToken, cells) => {
    if (cells) {
      const arr = cells.toIndexedSeq();
      const index = arr.indexOf(cellToken);
      if (index > -1) {
        return arr.get(index + 1);
      }
    }
    return null;
  }
) as any;

export const selectedPrevCellToken = createSelector(
  LegacyNotebookSelectors.selectedCellToken,
  selectedNotebookCellTokens,
  (cellToken, cells) => {
    if (cells) {
      const arr = cells.toIndexedSeq();
      const index = arr.indexOf(cellToken);
      if (index > 0) {
        return arr.get(index - 1);
      }
    }
  }
) as any;

export function createLastResultsByCellSelector(cellToken: string): Selector<any, NormalizedNotebookCellRunResult[]> {
  // Create a selector for pulling out the normalized results for the provided cell token
  const cellResultsSelector = createImmutableCheckSelector(
    (state: any) => runsResultTokensArray(state).get(cellLastRunTokensArray(state).get(cellToken)),
    (state: any) => state.client.notebooks.cellRunResults,
    (resultTokens, results) => {
      const mergedResults =
        (resultTokens &&
          results.tokens
            .intersect(resultTokens)
            .map((t: string) => getNormalizedCellRunResult(results, t))
            .toArray()) ||
        [];
      return mergedResults.filter((r: NormalizedNotebookCellRunResult) => r.content != null);
    }
  ) as any;

  // Return a memoized selector that will do a deep equality check to see if the results have changed
  return createResultCheckSelector(cellResultsSelector, (r) => r);
}

export const isPublishedDatasetCell = createSelector(
  (state: any, cellToken: string) => cellResourcesArray(state).get(cellToken),
  (cellResource) => isPresent(cellResource?.thoughtspot_published_at)
);

export function createRunnableCellTokensSelector(notebookToken: string) {
  return createSelector(
    (state: any) => LegacyNotebookSelectors.notebookCellTokensArray(state).get(notebookToken),
    cellFrozenArray,
    LegacyNotebookSelectors.cellContentTypesArray,
    cellContentsArray,
    (tokens, frozen, contentTypes, contents) =>
      tokens.filterNot((t: any) => frozen.get(t) || contentTypes.get(t) === 'markdown' || !contents.get(t))
  );
}
export const editingCellToken = createSelector(
  LegacyNotebookSelectors.selectedCellToken,
  isEditing,
  (cellToken, editing) => editing && cellToken
) as any;
